/**
 * Deep merges two objects, recursively merging nested objects.
 * Arrays and primitives from the source object will replace those in the target.
 *
 * @param {any} target - The target object to merge into
 * @param {any} source - The source object to merge from
 * @returns {any} The merged object
 */
export function deepMerge(target, source) {
  if (source === null || source === undefined) {
    return target;
  }

  if (typeof source !== 'object' || Array.isArray(source)) {
    return source;
  }

  if (typeof target !== 'object' || Array.isArray(target)) {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        typeof target[key] === 'object' &&
        target[key] !== null &&
        !Array.isArray(target[key])
      ) {
        result[key] = deepMerge(target[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}

export class IbdSkeleton extends HTMLElement {
  shimmer() {
    this.setAttribute('ibd-shimmer', '');
  }

  resetShimmer() {
    this.removeAttribute('ibd-shimmer');
  }
}

if (!customElements.get('ibd-skeleton')) {
  customElements.define('ibd-skeleton', IbdSkeleton);
}

/**
 * Resets the ibd-shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetIbdSkeleton(container = document.body) {
  const ibdSkeleton = container.querySelectorAll('ibd-skeleton');
  ibdSkeleton.forEach((item) => item.removeAttribute('ibd-shimmer'));
}

/**
 * Event types that count as a first user interaction.
 * `pointerdown` covers mouse, touch, and pen presses; `wheel` catches trackpad and
 * mouse-wheel scrolling before a `scroll` event fires. `mousemove` is deliberately
 * excluded: with a timeout fallback available it triggers near-instantly on desktop
 * and provides no real deferral.
 * @type {readonly string[]}
 */
const FIRST_INTERACTION_EVENT_TYPES = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];

/**
 * The shared one-shot latch. Armed lazily by the first `whenFirstInteraction()` caller.
 * @type {Promise<string> | null}
 */
let firstInteractionPromise = null;

/**
 * Arms document-level listeners and resolves with the interaction type on the first hit.
 * If the inline early-capture script in layout/theme.liquid recorded an interaction
 * before this module executed, resolves immediately.
 * @returns {Promise<string>}
 */
function armFirstInteractionListeners() {
  return new Promise((resolve) => {
    if (window.__ibdInteracted) {
      resolve('early');
      return;
    }

    const controller = new AbortController();
    /** @type {AddEventListenerOptions} */
    const listenerOptions = { passive: true, capture: true, once: true, signal: controller.signal };

    for (const type of FIRST_INTERACTION_EVENT_TYPES) {
      document.addEventListener(
        type,
        () => {
          controller.abort();
          resolve(type);
        },
        listenerOptions
      );
    }
  });
}

/**
 * Resolves on the first user interaction, or after `timeout` milliseconds — whichever
 * comes first — so deferred features still load for users who never interact.
 *
 * The underlying latch is armed once and shared across all callers; each caller only
 * races it against its own timeout. Late callers resolve immediately if the
 * interaction already happened.
 *
 * @param {Object} [options] - Configuration options
 * @param {number | null} [options.timeout=10000] - Max wait in milliseconds before resolving with 'timeout'. Pass null to wait for a real interaction only.
 * @returns {Promise<string>} The interaction event type, 'early' (interaction happened before this module executed), or 'timeout'.
 *
 * @example
 * // Defer a third-party script until the user shows signs of life (or 10 s pass)
 * whenFirstInteraction().then(() => injectScript());
 *
 * @example
 * // Wait for a real interaction only, with no deadline
 * whenFirstInteraction({ timeout: null }).then((type) => console.log(type));
 */
export function whenFirstInteraction({ timeout = 10000 } = {}) {
  firstInteractionPromise ??= armFirstInteractionListeners();

  if (timeout === null || timeout === undefined) {
    return firstInteractionPromise;
  }

  return Promise.race([
    firstInteractionPromise,
    new Promise((resolve) => {
      setTimeout(() => resolve('timeout'), timeout);
    }),
  ]);
}

/**
 * Loads the JSON-LD debugger (sections/ibd-json-ld-debugger.liquid) through the Section Rendering API. It loads only
 * with `?debug=1` in the URL and the "JSON-LD debugger" theme setting on, so shoppers never download it.
 */
async function loadJsonLdDebugger() {
  if (!window.Ibd?.jsonLdDebugger) return;
  if (new URLSearchParams(window.location.search).get('debug') !== '1') return;
  // The debugger's CSS hides it below 768px.
  if (window.matchMedia('(max-width: 768px)').matches) return;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', 'ibd-json-ld-debugger');

  try {
    // Section responses send no Cache-Control: skip the HTTP cache so back/forward never mounts a stale debugger.
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    // createContextualFragment keeps the fetched scripts executable. Mount the section's children, not Shopify's
    // `.shopify-section` wrapper: theme code that walks `.shopify-section` (jumbo-text, section-renderer) would take
    // the debugger for the page's last section.
    const fragment = document.createRange().createContextualFragment(await response.text());
    document.body.append(...(fragment.firstElementChild?.childNodes ?? []));
  } catch (error) {
    console.error('IBD | JSON-LD Debugger: could not load', error);
  }
}

void loadJsonLdDebugger();
